<template>
  <div v-if="cards.length">
    <div class="flex items-baseline gap-2 mb-2">
      <h2 class="text-base flex-1">
        {{ cards.length > 1 ? 'Cartes de fidélité' : 'Carte de fidélité' }}
      </h2>
      <span class="text-sm text-gray-500">
        {{ cards.length }} carte{{ cards.length > 1 ? 's' : '' }}
      </span>
    </div>

    <div v-if="labEntries.length > 1" class="flex flex-col gap-2">
      <loyalty-card-ring-strip
        :entries="sortedLabEntries"
        :selected-id="selectedEntry?.card.id ?? null"
        @select="handleSelect"
      />
      <loyalty-card-detail-pill
        v-if="selectedEntry"
        :card="selectedEntry.card"
        :analysis="selectedEntry.analysis"
        @activate="handleActivate"
        @manage="handleManage"
        @reward="handleReward"
      />
    </div>
    <loyalty-card-solo
      v-else-if="labEntries.length === 1"
      :card="labEntries[0].card"
      :analysis="labEntries[0].analysis"
      @activate="handleActivate"
      @manage="handleManage"
      @reward="handleReward"
    />

    <div
      v-if="groupingEntries.length"
      class="flex flex-col gap-2"
      :class="{'border-t border-dashed border-gray-300 mt-3 pt-3': labEntries.length > 0}"
    >
      <grouping-card-row
        v-for="entry in groupingEntries"
        :key="`grouping-${entry.card.id}`"
        :card="entry.card"
        :analysis="entry.analysis"
        @activate="handleActivate"
        @manage="handleManage"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import {computed, nextTick, ref, watch} from "vue";
import {DetectionLoyaltyCard, DetectionNotificationProps} from "@/notification/interfaces";
import LoyaltyCardRingStrip
    from "@/notification/components/notification-detection/loyalty-cards/LoyaltyCardRingStrip.vue";
import LoyaltyCardDetailPill
    from "@/notification/components/notification-detection/loyalty-cards/LoyaltyCardDetailPill.vue";
import LoyaltyCardSolo from "@/notification/components/notification-detection/loyalty-cards/LoyaltyCardSolo.vue";
import GroupingCardRow from "@/notification/components/notification-detection/loyalty-cards/GroupingCardRow.vue";
import {
    analyzeDetectionCard,
    DetectionCardEntry
} from "@/notification/components/notification-detection/loyalty-cards/detectionCardAnalysis";

const props = defineProps<{
    item: DetectionNotificationProps;
}>();

const emit = defineEmits<{
    'show-membership': [card: DetectionLoyaltyCard];
    /** Open the membership with the reward attribution dialog already open. */
    'show-reward': [card: DetectionLoyaltyCard];
    'link-membership': [card: DetectionLoyaltyCard];
    resize: [];
}>();

const selectedId = ref<number | null>(null);

const cards = computed(() => props.item.loyalty_cards ?? []);

/**
 * One analysis per card per recompute, shared with every child through
 * props: the ring, its pill and the sort all read the same numbers.
 */
const entries = computed<DetectionCardEntry[]>(() => cards.value.map(
    (card) => ({card, analysis: analyzeDetectionCard(card)})
));

/** Laboratory cards feed the ring strip; grouping cards get their own rows. */
const labEntries = computed(() => entries.value.filter((entry) => !entry.analysis.isGrouping));

const groupingEntries = computed(() => entries.value.filter((entry) => entry.analysis.isGrouping));

/** Reward-ready cards first, then actives by progress, inactives last. */
const sortedLabEntries = computed(() => {
    const rank = {done: 0, active: 1, inactive: 2};

    return [...labEntries.value].sort((a, b) => (
        rank[a.analysis.state] - rank[b.analysis.state]
        || b.analysis.percent - a.analysis.percent
        || a.analysis.shortName.localeCompare(b.analysis.shortName)
    ));
});

const selectedEntry = computed(() => (
    sortedLabEntries.value.find((entry) => entry.card.id === selectedId.value)
    ?? sortedLabEntries.value[0]
    ?? null
));

/** New detection: back to the default selection. */
watch(() => props.item, () => {
    selectedId.value = null;
});

/**
 * The popup window height is measured from scrollHeight: any content height
 * change (ring selection, activation turning a pill into a progress bar)
 * must be followed by a resize, or the window ends up cropped.
 */
const notifyResize = async () => {
    await nextTick();
    emit('resize');
};

watch(
    () => cards.value.map((card) => !!card.membership).join(),
    () => notifyResize()
);

/**
 * A ring click details the card; a second click on the ring already
 * detailed opens it, as the pharmacist expects from a card avatar.
 */
const handleSelect = (card: DetectionLoyaltyCard) => {
    if (selectedEntry.value?.card.id === card.id && card.membership) {
        handleManage(card);

        return;
    }

    selectedId.value = card.id;
    notifyResize();
};

const handleActivate = (card: DetectionLoyaltyCard) => {
    emit('link-membership', card);
};

const handleManage = (card: DetectionLoyaltyCard) => {
    emit('show-membership', card);
};

const handleReward = (card: DetectionLoyaltyCard) => {
    emit('show-reward', card);
};
</script>
